import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Api, StoredChatSession } from "../api.js";
import { BaseElement } from "../app.js";
import { closeIcon } from "../utils/icons.js";

@customElement("chat-history")
export class ChatHistory extends BaseElement {
    @property()
    onSelect?: (session: StoredChatSession) => void;

    @property()
    onClose?: () => void;

    @state()
    private sessions: StoredChatSession[] = [];

    @state()
    private loading = false;

    @state()
    private page = 0;

    @state()
    private hasMore = true;

    private observer: IntersectionObserver | null = null;

    async connectedCallback() {
        super.connectedCallback();
        this.loadMore();

        // Setup intersection observer for infinite scroll
        this.observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !this.loading && this.hasMore) {
                    this.loadMore();
                }
            },
            { threshold: 0.1 }
        );
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.observer?.disconnect();
    }

    private async loadMore() {
        if (this.loading || !this.hasMore) return;

        this.loading = true;
        try {
            const result = await Api.getChats({ page: this.page, pageSize: 20 });
            this.sessions = [...this.sessions, ...result.sessions];
            this.hasMore = this.sessions.length < result.total;
            this.page++;
        } catch (e) {
            console.error("Failed to load chat history:", e);
        } finally {
            this.loading = false;
        }
    }

    protected firstUpdated() {
        // Observe the loader element for infinite scroll
        const loader = this.renderRoot.querySelector(".loader");
        if (loader) {
            this.observer?.observe(loader);
        }
    }

    render() {
        return html`
            <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div class="bg-white rounded-lg shadow-xl w-full max-w-lg">
                    <div class="flex items-center justify-between p-4 border-b">
                        <h2 class="text-lg font-medium">Chat History</h2>
                        <button @click=${this.onClose} class="text-gray-400 hover:text-gray-600">
                            <i class="w-5 h-5 icon">${closeIcon}</i>
                        </button>
                    </div>
                    <div class="max-h-[60vh] overflow-y-auto divide-y">
                        ${this.sessions.map(
                            (session) => html`
                                <div class="w-full text-left p-3 hover:bg-gray-50 relative cursor-pointer" @click=${() => this.onSelect?.(session)}>
                                    <div class="flex justify-between items-center">
                                        <div class="flex-1 min-w-0 pr-8">
                                            <div class="text-sm font-medium text-gray-900 truncate max-w-full">
                                                ${session.messages.find((m) => m.role === "user")?.content || "Empty chat"}
                                            </div>
                                            <div class="text-xs text-gray-500 mt-1">${new Date(session.last_modified).toLocaleString()}</div>
                                        </div>
                                        <button
                                            @click=${(e: Event) => this.handleDelete(e, session)}
                                            class="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600"
                                            title="Delete chat"
                                        >
                                            <i class="w-4 h-4 icon">${closeIcon}</i>
                                        </button>
                                    </div>
                                </div>
                            `
                        )}
                        <div class="loader h-8 flex items-center justify-center">
                            ${this.loading ? html`<span class="text-sm text-gray-500">Loading...</span>` : null}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    private async handleDelete(e: Event, session: StoredChatSession) {
        e.stopPropagation(); // Prevent session selection
        try {
            await Api.deleteChat(session.id);
            this.sessions = this.sessions.filter((s) => s.id !== session.id);
        } catch (e) {
            console.error("Failed to delete chat:", e);
        }
    }
}
